import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { EditPenIcon, DropdownToggleIcon, InvoiceIcon, ChevronRightIcon } from "../../../../../components/icons";

const visa_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/visa.svg";
const master_card_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/master-card.svg";
const jcb_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/jcb.svg";
const american_ex_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/american_ex.svg";

const PaymentMethodInformation = ({
    authDetailBooking,
    typo12,
    typo14,
    formatDatePayment
}) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const [showDetailStates, setShowDetailStates] = useState({});

    const getShowDetail = (key) => showDetailStates[key] || false;
    const toggleShowDetail = (key) => setShowDetailStates(prev => ({ ...prev, [key]: !prev[key] }));

    const convertToMMYYYY = (expirationMonth, expirationYear) => {
        const mm = expirationMonth?.padStart(2, "0");
        const yy = expirationYear?.slice(-2);

        const formattedDate = `${mm}/${yy}`;
        return formattedDate;
    }

    const getCardType = (brand) => {
        switch (brand) {
            case "VISA":
                return visa_icon;
                break;

            case "MASTERCARD":
                return master_card_icon;
                break;

            case "JCB":
                return jcb_icon;
                break;

            case "AMEX":
                return american_ex_icon;
                break;

            default:
                break;
        }
    }

    // An edit approved while the booking was still unpaid charges the whole new price
    // against the booking itself. Its previousBooking snapshot was never charged, so the
    // booking — not the snapshot — holds the payment, and that charge is the booking's own
    // rather than a modification on top of an earlier payment.
    const isUnpaidSnapshot = (request) =>
        request?.requestType === "reschedule" &&
        String(request?.previousBooking?.paymentStatus || "").toUpperCase() === "CHARGE_PENDING";

    const getPaymentInfoList = (booking) => {
        const paymentInfoList = [];

        if (booking?.user?.deferredPaymentEligible) {
            const filteredItems = booking?.invoice?.items?.filter(item =>
                item.refId === booking?._id || item.bookingId === booking?._id
            );

            filteredItems?.map((item, index) => {
                const confirmDate = moment(item?.addedAt).local().format(formatDatePayment[currentLanguage]);
                let confirmInfo = "";
                let confirmTitle = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeTitle.priceUpdated");
                const isLastItem = index === filteredItems.length - 1;

                if (item?.type === "booking") {
                    confirmInfo = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeStatus.bookingConfirmed");
                    confirmTitle = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeTitle.addedToMonthlyInvoice");
                } else if (item?.type === "bookingeditfee") {
                    confirmInfo = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeStatus.bookingConfirmedWithPriceAdjustment");
                    confirmTitle = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeTitle.addedToMonthlyInvoice");
                } else if (item?.type === "additionalfee") {
                    confirmInfo = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeStatus.additionalFeeCharged");
                    if (!isLastItem) confirmTitle = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeTitle.addedAdditionalFeeToMonthlyInvoice");
                } else if (item?.type === "cancelfee") {
                    confirmInfo = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeStatus.modificationRequestApproved");
                    if (!isLastItem) confirmTitle = t("bookingDetail.paymentMethodInformation.deferredPayment.chargeTitle.addedToMonthlyInvoice");
                }

                const confirmAmountStyle = !isLastItem ? "tw:!text-[15px] tw:!font-[500] tw:!mb-0 tw:!text-[#8D8D8D] tw:!line-through" : "tw:!text-[15px] tw:!font-[400] tw:!mb-0 tw:!text-[#282828]"

                paymentInfoList.push({
                    confirmTitle: confirmTitle,
                    confirmAmount: `${isLastItem ? (filteredItems.reduce((acc, curr) => acc + curr.amount, 0)).toLocaleString() : item?.amount.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                    confirmAmountStyle: confirmAmountStyle,
                    confirmInfo: `${confirmDate} ${confirmInfo}`,
                    useDetail: item.type === "bookingeditfee" || item.type === "additionalfee",
                    showStatus: getShowDetail(`deferred_${item.type}_${index}`),
                    setShowStatus: (val) => toggleShowDetail(`deferred_${item.type}_${index}`),
                    detailInfo: {
                        useHeader: item.type === "additionalfee",
                        header: t("bookingDetail.paymentMethodInformation.deferredPayment.headerReasonAdditionalFee"),
                        title: item.type === "bookingeditfee" ? t("bookingDetail.paymentMethodInformation.deferredPayment.reasonPriceAdjustmentTitle") : t("bookingDetail.paymentMethodInformation.deferredPayment.reasonAdditionalFeeTitle"),
                        body: item.type === "bookingeditfee" ? booking?.confirmWithEditedFeeDetailItinerary : booking?.confirmAdditionalFeeDetailItinerary
                    }
                });

            });
        } else {
            if (authDetailBooking?.requestChangeBooking?.some(item => item.status === "APPROVED")) {
                authDetailBooking?.requestChangeBooking?.filter(item => item.status === "APPROVED")?.sort((a, b) => new Date(a.date) - new Date(b.date))?.map((item, index) => {
                    // previousBooking only holds payment data when it was actually paid.
                    const bookingData = isUnpaidSnapshot(item) ? authDetailBooking : item?.previousBooking;
                    const responseBy = item?.responseBy;

                    if (!responseBy) {
                        if (index === authDetailBooking?.requestChangeBooking?.length - 1 && paymentInfoList.length === 0) {
                            const bookingData = authDetailBooking;

                            const chargeDate = bookingData?.flywireChargedAt ? moment(bookingData?.flywireChargedAt).local().format(formatDatePayment[currentLanguage]) : "";
                            const chargeInfo = bookingData?.editedFeeType === "CHARGE_EDITED_FEE" ? t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.bookingConfirmedWithPriceAdjustment") : t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.bookingConfirmed");

                            paymentInfoList.push({
                                chargeTitleDate: chargeDate,
                                chargeTitleInfo: chargeInfo,
                                chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeResponse?.amount?.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                                useDetail: bookingData?.editedFeeType === "CHARGE_EDITED_FEE",
                                showStatus: getShowDetail('editfee_main'),
                                setShowStatus: (val) => toggleShowDetail('editfee_main'),
                                detailInfo: {
                                    useHeader: false,
                                    header: "",
                                    title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonPriceAdjustmentTitle"),
                                    body: bookingData?.chargeWithEditedFeeDetailItinerary
                                }
                            });
                        }
                    } else {
                        if (paymentInfoList.length === 0) {
                            const chargeDate = bookingData?.flywireChargedAt ? moment(bookingData?.flywireChargedAt).local().format(formatDatePayment[currentLanguage]) : "";
                            const chargeInfo = bookingData?.editedFeeType === "CHARGE_EDITED_FEE" ? t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.bookingConfirmedWithPriceAdjustment") : t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.bookingConfirmed");
                            paymentInfoList.push({
                                chargeTitleDate: chargeDate,
                                chargeTitleInfo: chargeInfo,
                                chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeResponse?.amount?.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                                useDetail: bookingData?.editedFeeType === "CHARGE_EDITED_FEE",
                                showStatus: getShowDetail(`editfee_${index}`),
                                setShowStatus: (val) => toggleShowDetail(`editfee_${index}`),
                                detailInfo: {
                                    useHeader: false,
                                    header: "",
                                    title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonPriceAdjustmentTitle"),
                                    body: bookingData?.chargeWithEditedFeeDetailItinerary
                                }
                            });

                            if (bookingData?.chargeAdditionalFee) {
                                const chargeAdditionalFeeDate = bookingData?.flywireChargeAdditionalFeeAt ? moment(bookingData?.flywireChargeAdditionalFeeAt).local().format(formatDatePayment[currentLanguage]) : "";
                                paymentInfoList.push({
                                    chargeTitleDate: chargeAdditionalFeeDate,
                                    chargeTitleInfo: t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.additionalFeeCharged"),
                                    chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeAdditionalFeeResponse?.amount.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                                    useDetail: true,
                                    showStatus: getShowDetail(`additionalfee_change_${index}`),
                                    setShowStatus: (val) => toggleShowDetail(`additionalfee_change_${index}`),
                                    detailInfo: {
                                        useHeader: true,
                                        header: t("bookingDetail.paymentMethodInformation.advancePayment.headerReasonAdditionalFee"),
                                        title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonAdditionalFeeTitle"),
                                        body: bookingData?.chargeAdditionalFeeDetailItinerary
                                    }
                                });
                            }
                        }
                    }

                    if (bookingData?.chargeAdditionalFee) {
                        const chargeAdditionalFeeDate = bookingData?.flywireChargeAdditionalFeeAt ? moment(bookingData?.flywireChargeAdditionalFeeAt).local().format(formatDatePayment[currentLanguage]) : "";
                        paymentInfoList.push({
                            chargeTitleDate: chargeAdditionalFeeDate,
                            chargeTitleInfo: t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.additionalFeeCharged"),
                            chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeAdditionalFeeResponse?.amount.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                            useDetail: true,
                            showStatus: getShowDetail(`additionalfee_change_${index}`),
                            setShowStatus: (val) => toggleShowDetail(`additionalfee_change_${index}`),
                            detailInfo: {
                                useHeader: true,
                                header: t("bookingDetail.paymentMethodInformation.advancePayment.headerReasonAdditionalFee"),
                                title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonAdditionalFeeTitle"),
                                body: bookingData?.chargeAdditionalFeeDetailItinerary
                            }
                        });
                    }
                    const editDetail = item;
                    // A full charge from an edit is the booking's own payment, already listed
                    // above — listing it again would double-count it.
                    if (item?.paymentStatus === "CHARGED" && !isUnpaidSnapshot(item)) {
                        const chargeEditBooking = editDetail?.chargedAt ? moment(editDetail?.chargedAt).local().format(formatDatePayment[currentLanguage]) : "";
                        paymentInfoList.push({
                            chargeTitleDate: chargeEditBooking,
                            chargeTitleInfo: t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.modificationRequestApproved"),
                            chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${editDetail?.flywireChargeResponse?.amount?.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                            useDetail: false,
                            showStatus: false,
                            setShowStatus: () => { },
                            detailInfo: {
                                useHeader: false,
                                header: "",
                                title: "",
                                body: ""
                            }
                        });
                    }
                })

                if (paymentInfoList.length === 0) {
                    const bookingData = authDetailBooking;

                    const chargeDate = bookingData?.flywireChargedAt ? moment(bookingData?.flywireChargedAt).local().format(formatDatePayment[currentLanguage]) : "";
                    const chargeInfo = bookingData?.editedFeeType === "CHARGE_EDITED_FEE" ? t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.bookingConfirmedWithPriceAdjustment") : t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.bookingConfirmed");

                    paymentInfoList.push({
                        chargeTitleDate: chargeDate,
                        chargeTitleInfo: chargeInfo,
                        chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeResponse?.amount?.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                        useDetail: bookingData?.editedFeeType === "CHARGE_EDITED_FEE",
                        showStatus: getShowDetail('editfee_main'),
                        setShowStatus: (val) => toggleShowDetail('editfee_main'),
                        detailInfo: {
                            useHeader: false,
                            header: "",
                            title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonPriceAdjustmentTitle"),
                            body: bookingData?.chargeWithEditedFeeDetailItinerary
                        }
                    });

                    if (bookingData?.chargeAdditionalFee) {
                        const chargeAdditionalFeeDate = bookingData?.flywireChargeAdditionalFeeAt ? moment(bookingData?.flywireChargeAdditionalFeeAt).local().format(formatDatePayment[currentLanguage]) : "";
                        paymentInfoList.push({
                            chargeTitleDate: chargeAdditionalFeeDate,
                            chargeTitleInfo: t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.additionalFeeCharged"),
                            chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeAdditionalFeeResponse?.amount.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                            useDetail: true,
                            showStatus: getShowDetail('additionalfee_main'),
                            setShowStatus: (val) => toggleShowDetail('additionalfee_main'),
                            detailInfo: {
                                useHeader: true,
                                header: t("bookingDetail.paymentMethodInformation.advancePayment.headerReasonAdditionalFee"),
                                title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonAdditionalFeeTitle"),
                                body: bookingData?.chargeAdditionalFeeDetailItinerary
                            }
                        });
                    }
                }

                const bookingData = authDetailBooking;

                if (bookingData?.chargeAdditionalFee) {
                    const chargeAdditionalFeeDate = bookingData?.flywireChargeAdditionalFeeAt ? moment(bookingData?.flywireChargeAdditionalFeeAt).local().format(formatDatePayment[currentLanguage]) : "";
                    paymentInfoList.push({
                        chargeTitleDate: chargeAdditionalFeeDate,
                        chargeTitleInfo: t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.additionalFeeCharged"),
                        chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeAdditionalFeeResponse?.amount.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                        useDetail: true,
                        showStatus: getShowDetail('additionalfee_current'),
                        setShowStatus: (val) => toggleShowDetail('additionalfee_current'),
                        detailInfo: {
                            useHeader: true,
                            header: t("bookingDetail.paymentMethodInformation.advancePayment.headerReasonAdditionalFee"),
                            title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonAdditionalFeeTitle"),
                            body: bookingData?.chargeAdditionalFeeDetailItinerary
                        }
                    });
                }
            } else {
                const bookingData = authDetailBooking;

                const chargeDate = bookingData?.flywireChargedAt ? moment(bookingData?.flywireChargedAt).local().format(formatDatePayment[currentLanguage]) : "";
                const chargeInfo = bookingData?.editedFeeType === "CHARGE_EDITED_FEE" ? t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.bookingConfirmedWithPriceAdjustment") : t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.bookingConfirmed");

                paymentInfoList.push({
                    chargeTitleDate: chargeDate,
                    chargeTitleInfo: chargeInfo,
                    chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeResponse?.amount?.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                    useDetail: bookingData?.editedFeeType === "CHARGE_EDITED_FEE",
                    showStatus: getShowDetail('editfee_main'),
                    setShowStatus: (val) => toggleShowDetail('editfee_main'),
                    detailInfo: {
                        useHeader: false,
                        header: "",
                        title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonPriceAdjustmentTitle"),
                        body: bookingData?.chargeWithEditedFeeDetailItinerary
                    }
                });

                if (bookingData?.chargeAdditionalFee) {
                    const chargeAdditionalFeeDate = bookingData?.flywireChargeAdditionalFeeAt ? moment(bookingData?.flywireChargeAdditionalFeeAt).local().format(formatDatePayment[currentLanguage]) : "";
                    paymentInfoList.push({
                        chargeTitleDate: chargeAdditionalFeeDate,
                        chargeTitleInfo: t("bookingDetail.paymentMethodInformation.advancePayment.chargeStatus.additionalFeeCharged"),
                        chargeAmount: `${t("bookingDetail.paymentMethodInformation.advancePayment.charged")} ${bookingData?.flywireChargeAdditionalFeeResponse?.amount.toLocaleString()}${t("bookingDetail.paymentMethodInformation.jpy")}`,
                        useDetail: true,
                        showStatus: getShowDetail('additionalfee_main'),
                        setShowStatus: (val) => toggleShowDetail('additionalfee_main'),
                        detailInfo: {
                            useHeader: true,
                            header: t("bookingDetail.paymentMethodInformation.advancePayment.headerReasonAdditionalFee"),
                            title: t("bookingDetail.paymentMethodInformation.advancePayment.reasonAdditionalFeeTitle"),
                            body: bookingData?.chargeAdditionalFeeDetailItinerary
                        }
                    });
                }
            }
        }

        return paymentInfoList;
    }

    return (
        <>
            {
                !authDetailBooking?.user?.deferredPaymentEligible ? (
                    <div className="tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-white tw:lg:px-7 tw:py-[30px]">
                        <div className="tw:font-hanken-grotesk tw:pb-6 tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-[#282828] tw:px-5 tw:lg:px-0">
                            {t("bookingDetail.paymentMethodInformation.advancePayment.title")}
                        </div>
                        <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />
                        <div className="tw:flex tw:flex-col tw:pt-6 tw:px-5 tw:lg:px-0">
                            <div className="tw:flex tw:items-center tw:justify-between">
                                <div className="tw:flex tw:items-center tw:gap-2">
                                    <img src={getCardType(authDetailBooking?.flywireResponse?.brand)} alt="Card" className="tw:h-[11px] tw:w-[37px]" />
                                    <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                        {t("bookingDetail.paymentMethodInformation.advancePayment.cardNumber", {
                                            digits: authDetailBooking?.flywireResponse?.digits,
                                            expiryDate: convertToMMYYYY(authDetailBooking?.flywireResponse?.expirationMonth, authDetailBooking?.flywireResponse?.expirationYear)
                                        })}
                                    </p>
                                </div>
                                {
                                    authDetailBooking.paymentStatus === "CHARGE_PENDING" && (
                                        <span className={`tw:font-hanken-grotesk tw:text-[#7c7b7b] ${typo14}`}>
                                            {t("bookingDetail.paymentMethodInformation.advancePayment.notCharged")}
                                        </span>
                                    )
                                }
                            </div>
                            {
                                authDetailBooking.paymentStatus === "CHARGE_PENDING" ? (
                                    <>
                                        {/* <button
                                        className="tw:flex tw:!mt-6"
                                        type="button"
                                    >
                                        <div className={`tw:inline-flex tw:items-center tw:gap-2 tw:self-start tw:rounded-[5px] tw:border tw:border-[#d9d9d9] tw:bg-white tw:px-4 tw:py-2 tw:text-[#282828] ${typo14}`}>
                                            <EditPenIcon
                                                color="#282828"
                                                size={14}
                                            />
                                            <span className="tw:font-hanken-grotesk tw:font-[400]">{t("bookingDetail.paymentMethodInformation.advancePayment.changeCard")}</span>
                                        </div>
                                    </button> */}
                                    </>
                                ) : (
                                    <>
                                        <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8] tw:!my-6" />
                                        {
                                            getPaymentInfoList(authDetailBooking).map((item, index) => (
                                                <div key={index} className={`tw:flex tw:flex-wrap tw:items-start tw:justify-between ${index === getPaymentInfoList(authDetailBooking).length - 1 ? "tw:!mb-0" : "tw:!mb-2"}`}>
                                                    <div className="tw:flex tw:items-center">
                                                        <p className={`tw:!mb-0 tw:!text-[13px] tw:!text-[400] tw:!text-[#8D8D8D]`}>
                                                            <span>{item?.chargeTitleDate}</span>
                                                            <br className="tw:block tw:md:hidden" />
                                                            <span className="tw:hidden tw:md:inline">&nbsp;</span>
                                                            <span>{item?.chargeTitleInfo}</span>
                                                        </p>
                                                    </div>

                                                    <div
                                                        className={`tw:flex tw:items-center tw:gap-2 ${item?.useDetail ? "tw:cursor-pointer" : ""}`}
                                                        onClick={() => item?.setShowStatus(!item?.showStatus)}
                                                    >
                                                        <span className={`tw:!mb-0 tw:!text-[14px] tw:!text-[400] tw:!text-[#8D8D8D]`}>
                                                            {item?.chargeAmount}
                                                        </span>
                                                        {
                                                            item?.useDetail && (
                                                                <div className={`tw:transition-transform tw:duration-300 ${item?.showStatus ? "tw:rotate-180" : "tw:rotate-0"}`}>
                                                                    <DropdownToggleIcon
                                                                        size={12}
                                                                        color={"#A7A7A7"}
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                    </div>

                                                    {
                                                        item?.useDetail && (
                                                            <div className={`tw:grid tw:transition-all tw:duration-300 tw:ease-in-out tw:w-full ${item?.showStatus ? "tw:grid-rows-[1fr] tw:!mb-2" : "tw:grid-rows-[0fr] tw:!mb-0"}`}>
                                                                <div className={`tw:overflow-hidden ${item?.showStatus ? "tw:!mt-2" : "tw:!mt-0"}`}>
                                                                    <div className="tw:!bg-[#f9f9f9] tw:!rounded-[8px] tw:!p-4">
                                                                        {
                                                                            item?.detailInfo?.useHeader && (
                                                                                <div className="tw:!mb-4 tw:border-b tw:border-[#e0e0e0] tw:border-dashed">
                                                                                    <p className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[13px] tw:!mb-2 tw:!italic">{item?.detailInfo?.header}</p>
                                                                                </div>
                                                                            )
                                                                        }
                                                                        <div className="tw:flex-1">
                                                                            <p className="tw:!text-[#8D8D8D] tw:!font-[700] tw:!text-[12px] tw:!mb-4">{item?.detailInfo?.title}</p>
                                                                            <p className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px] tw:!mb-0">
                                                                                {item?.detailInfo?.body}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            ))
                                        }
                                    </>
                                )
                            }
                        </div>
                    </div>
                ) : (
                    <div className="tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-white tw:lg:px-7 tw:py-[30px]">
                        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-[14px] tw:px-5 tw:lg:px-0">
                            <div className="tw:font-hanken-grotesk tw:pb-6 tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-[#282828]">
                                {t("bookingDetail.paymentMethodInformation.deferredPayment.title")}
                            </div>
                            {/* <a href="#" className={`tw:inline-flex tw:items-center tw:gap-2 tw:pb-6`}>
                                <InvoiceIcon
                                    width={14}
                                    height={15}
                                    color="#4E4E4E"
                                />
                                <span className={`tw:text-[14px] tw:text-[400] tw:text-[#4E4E4E]`}>
                                    {t("bookingDetail.paymentMethodInformation.deferredPayment.invoiceAndBilling")}
                                </span>
                                <ChevronRightIcon
                                    width={5}
                                    height={7}
                                    color="#8D8D8D"
                                />
                            </a> */}
                        </div>
                        <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />
                        <div className="tw:flex tw:flex-col tw:gap-[20px] tw:pt-6 tw:px-5 tw:lg:px-0">
                            {authDetailBooking?.paymentStatus === "ADDED_TO_MONTHLY_TOTAL" ? (
                                <>
                                    {
                                        getPaymentInfoList(authDetailBooking).map((item, index) => (
                                            <div key={index}>
                                                <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between">
                                                    <div className="tw:flex tw:items-center tw:gap-[14px]">
                                                        <p className="tw:!mb-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">
                                                            {item?.confirmTitle}
                                                        </p>
                                                        <p className={item?.confirmAmountStyle}>
                                                            {item?.confirmAmount}
                                                        </p>
                                                    </div>

                                                    <div
                                                        className={`tw:flex tw:items-center tw:gap-2 ${item?.useDetail ? "tw:cursor-pointer" : ""}`}
                                                        onClick={() => item?.setShowStatus(!item?.showStatus)}
                                                    >
                                                        <span className={`tw:!mb-0 tw:!text-[14px] tw:!text-[400] tw:!text-[#8D8D8D]`}>
                                                            {item?.confirmInfo}
                                                        </span>
                                                        {
                                                            item?.useDetail && (
                                                                <div className={`tw:transition-transform tw:duration-300 ${item?.showStatus ? "tw:rotate-180" : "tw:rotate-0"}`}>
                                                                    <DropdownToggleIcon
                                                                        size={12}
                                                                        color={"#A7A7A7"}
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                    </div>

                                                    {
                                                        item?.useDetail && (
                                                            <div className={`tw:grid tw:transition-all tw:duration-300 tw:ease-in-out tw:w-full ${item?.showStatus ? "tw:grid-rows-[1fr] tw:!mb-2" : "tw:grid-rows-[0fr] tw:!mb-0"}`}>
                                                                <div className={`tw:overflow-hidden ${item?.showStatus ? "tw:!mt-2" : "tw:!mt-0"}`}>
                                                                    <div className="tw:!bg-[#f9f9f9] tw:!rounded-[8px] tw:!p-4">
                                                                        {
                                                                            item?.detailInfo?.useHeader && (
                                                                                <div className="tw:!mb-4 tw:border-b tw:border-[#e0e0e0] tw:border-dashed">
                                                                                    <p className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[13px] tw:!mb-2 tw:!italic">{item?.detailInfo?.header}</p>
                                                                                </div>
                                                                            )
                                                                        }
                                                                        <div className="tw:flex-1">
                                                                            <p className="tw:!text-[#8D8D8D] tw:!font-[700] tw:!text-[12px] tw:!mb-4">{item?.detailInfo?.title}</p>
                                                                            <p className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px] tw:!mb-0">
                                                                                {item?.detailInfo?.body}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        ))
                                    }

                                    <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />

                                    <p className="tw:!mb-0 tw:!text-[14px] tw:!font-[400] tw:!text-[#7C7B7B] tw:!italic">
                                        {t("bookingDetail.paymentMethodInformation.deferredPayment.note", {
                                            monthYear: moment(authDetailBooking?.addedToMonthlyTotalAt).clone().add(1, "month").format("MMMM YYYY")
                                        })}
                                    </p>
                                </>
                            ) : (
                                <p className="tw:!mb-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">
                                    {t("bookingDetail.paymentMethodInformation.deferredPayment.notAddedToMonthlyInvoice")}
                                </p>
                            )}
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default PaymentMethodInformation;